import {
  Box,
  Button as MuiButton,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
  Modal,
  Tooltip,
  AlertTitle,
  DialogActions,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, ButtonVariantOption } from "../../../components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle, SectionTitle } from "@/components/Title";
import { KeyboardArrowLeft } from "@mui/icons-material";
import React, { useState, useEffect } from "react";
import { useFeatureFlags } from "@/configs/featureFlag";
import { MultiSelect } from "@/components/Select";
import { AREA_USE_PURPOSE_DATA_SOURCE } from "@/constants/areaUsePurposeDataSource";
import { Divider } from "@/components/Divider";
import { Label } from "@/components/Label";
import { z } from "zod";
import { getYear } from "date-fns";
import { usePrefectures } from "@/hooks/usePrefectures";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCityWithExternalState } from "@/hooks/useCity";
import { useReceptionReasonOptions } from "@/features/realEstateReceptionBookFeed/hooks/useReceptionReasonOptions";
import { useAreaUsePurposeOptions } from "@/hooks/useAreaUsePurposeOptions";
import { useHotelCategoryOptions } from "@/hooks/useHotelCategoryOptions";
import {
  getSearchMaxDate,
  getSearchMinDate,
} from "@/features/realEstateReceptionBookFeed/utils/dateTime";
import { CMultiSelect } from "@/components/Select/CMultiSelect";
import { DateRangePicker } from "@/components/DatePicker";
import { FeedGrid } from "../components/FeedGrid";
import RealEstateDetailModal from "@/features/titleSearch/components/RealEstateDetailModal";
import { useApiClient } from "@/hooks/useApiClient";
import {
  type RealEstateTitleSearchAPIResponseStatus,
  type RealEstateTitleSearchDataGridRow,
  type RealEstateTitleSearchAPIRequest,
} from "../types";
import { type UserSearchHistoryTitleResponseType } from "../types/";
import { useAcquireBookForm } from "@/features/acquireMultipleBooks/hooks/useAcquireBookForm";
import { type AcquireMultipleBookHandoffData } from "@/types/acquirebook";
import {
  AcquireMultipleBooksModal,
  getBookTypeFromRealEstateTypeLabel,
} from "@/features/map/components/AcquireMultipleBooksModal";
import { HttpStatusCode } from "axios";
import { feedAPI } from "../api";
import { useGridPagination } from "../hooks/useGridPagination";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import {
  makeRangeSchemaWithRestrictMinMax,
  makeIntegerRangeSchema,
  makeAreaRangeSchema,
  makeBigIntRangeSchema,
  makeNullableDateRangeSchema,
} from "@/utils/utilsReactHookForms";
import { SearchHistoryModal } from "../components/SearchHistoryModal";
import CloseIcon from "@mui/icons-material/Close";
import { LandSearchAccordion } from "../components/LandSearchAccordion";
import { BuildingSearchAccordion } from "../components/BuildingSearchAccordion";
import { type FormData } from "../types/formTypes";
import { formatNumberWithComma } from "@/utils/formatNumber";

// 不動産種別のマップ
export const REAL_ESTATE_TYPE_MAP = {
  land: { label: "土地", value: "land" },
  kubunBuilding: { label: "区分建物", value: "kubun_building" },
  building: { label: "建物", value: "building" },
};
// 容積率で入力可能な最小値、最小値を定義する。
const YOUSEKI_RATIO_MIN = 50;
const YOUSEKI_RATIO_MAX = 1300;
// 建ぺい率で入力可能な最小値、最小値を定義する。
const KENPEI_RATIO_MIN = 30;
const KENPEI_RATIO_MAX = 80;
// 築年数で入力可能な最小値を定義する。
const BUILDING_AGE_MIN = 0;
// 築年数で入力可能な最大値を定義する。
// BEのコードで、現在日時のyearからMAX+1を引いた年をstart_dateとしている。
// datetime()のyearに0以下は指定できないので、最大値は現在日時のyear-2とする。
const BUILDING_AGE_MAX = getYear(new Date()) - 2;
// 地上階で入力可能な最小値、最大値を定義する。
const GROUND_FLOORS_MIN = 0;
const GROUND_FLOORS_MAX = 100;
// 地下階で入力可能な最小値、最大値を定義する。
const UNDERGROUND_FLOORS_MIN = 0;
const UNDERGROUND_FLOORS_MAX = 100;
// 駐車場数で入力可能な最小値、最大値を定義する。
const PARKING_SPACES_MIN = 0;
const PARKING_SPACES_MAX = 1000;
// 家屋番号数で入力可能な最小値、最大値を定義する。
const KAOKU_NUMBER_MIN = 0;
const KAOKU_NUMBER_MAX = 10000;
// ホテル部屋数で入力可能な最小値、最大値を定義する。
const HOTEL_ROOM_COUNT_MIN = 0;
const HOTEL_ROOM_COUNT_MAX = 10000;
// 文字列フィールドの最大文字数を定義する。
const ADDRESS_MAX_LENGTH = 255; // 大字・町丁目
const OWNER_NAME_MAX_LENGTH = 255; // 所有者名
const OWNER_ADDRESS_MAX_LENGTH = 255; // 所有者住所
const PROPERTY_NAME_MAX_LENGTH = 255; // 建物名
const HOTEL_NAME_MAX_LENGTH = 255; // ホテル名
const DORMITORY_NAME_MAX_LENGTH = 255; // 寮・社宅名
const INDUSTRIAL_FACILITY_NAME_MAX_LENGTH = 255; // 産業施設名
const DORMITORY_KIND_VALUES = ["分譲", "一棟", "不明"] as const;
const DORMITORY_KIND_OPTIONS = DORMITORY_KIND_VALUES.map((value) => ({
  label: value,
  value,
}));
const PARKING_LOT_NAME_MAX_LENGTH = 255; // 駐車場名
// 築年月で入力可能な最小値を定義する。
const BUILD_DATE_MIN = new Date(1900, 0, 1);
// FeedGridに渡すIDのためのデリミタ
export const ID_DELIMITER = "___";
// 売買スクリーニングが処理が可能な最大件数
const MAX_HAS_TRADE_COUNT = 1000;

export const TitleSearch: React.FC = () => {
  const {
    mypageTop,
    realEstateTitle,
    hotelSearch,
    dormitorySearch,
    cityPlanningAreaUsePurposeSearch,
    industrialFacilitySearch,
  } = useFeatureFlags();
  const hotelSearchEnabled = Boolean(hotelSearch);
  const dormitorySearchEnabled = Boolean(dormitorySearch);
  const industrialFacilitySearchEnabled = Boolean(industrialFacilitySearch);
  const apiClient = useApiClient().apiClient;
  const { page, pageSize, changePage, changePageSize, resetPage } =
    useGridPagination();
  const rowsPerPageOptions: number[] = [25, 50, 100];
  const { handleSubmit: acquireBookFormHandleSubmit } = useAcquireBookForm();

  // Feature Flagに基づいてデフォルトのデータソースを決定
  // cityPlanningAreaUsePurposeSearchがtrueの場合は都市計画決定GISデータ、falseまたはundefinedの場合は国土数値情報
  const defaultAreaUsePurposeDataSource = cityPlanningAreaUsePurposeSearch
    ? AREA_USE_PURPOSE_DATA_SOURCE.CITY_PLANNING
    : AREA_USE_PURPOSE_DATA_SOURCE.NATIONAL_LAND;

  // 都道府県一覧
  const { prefectures } = usePrefectures();

  // 築年月の選択可能最大値
  const maxDate = getSearchMaxDate();
  // 法務局受付日の選択可能最小値
  const requestDateMin = getSearchMinDate();

  // 用途地域一覧を取得
  const { areaUsePurposeOptions } = useAreaUsePurposeOptions();

  // ホテル形態一覧を取得
  const { hotelCategoryOptions } = useHotelCategoryOptions();

  // 登記原因一覧を取得
  const { receptionReasonOptions } = useReceptionReasonOptions();

  // 表題部検索の結果一覧
  const [realEstateTitles, setRealEstateTitles] = useState<
    RealEstateTitleSearchDataGridRow[]
  >([]);

  // 登記図面取得モーダル用のState
  const [acquireMultipleBooksRows, setAcquireMultipleBooksRows] = useState<
    AcquireMultipleBookHandoffData[]
  >([]);

  // 登記図面取得モーダル表示制御用のState
  const [openAcquireMultipleBooksModal, setOpenAcquireMultipleBooksModal] =
    useState<boolean>(false);

  // 売買スクリーニングのスイッチの状態
  const [switchHasTrade, setSwitchHasTrade] = useState<boolean>(false);

  // 売買スクリーニングのスイッチの活性/非活性
  const [, setSwitchDisabled] = useState<boolean>(true);

  // 詳細ダイアログの表示
  const [openRealEstateDetailModal, setOpenRealEstateDetailModal] =
    useState<boolean>(false);

  // 詳細ダイアログに表示するデータのID
  const [detailData, setDetailData] = useState<Array<{
    title: string;
    value: string | string[];
  }> | null>(null);

  // 検索時のローディング
  const [loading, setLoading] = useState<boolean>(false);

  // 検索履歴モーダルの表示
  const [openSearchHistoryModal, setOpenSearchHistoryModal] = useState(false);

  const FormDataSchema = z.object({
    prefecture: z.array(z.number()).nonempty("都道府県を選択してください"),
    city: z.array(z.number()).nonempty("市区町村を選択してください"),
    address: z
      .string()
      .max(
        ADDRESS_MAX_LENGTH,
        `${ADDRESS_MAX_LENGTH}文字以内で入力してください`
      ),
    areaUsePurposeDataSource: z.enum([
      AREA_USE_PURPOSE_DATA_SOURCE.CITY_PLANNING,
      AREA_USE_PURPOSE_DATA_SOURCE.NATIONAL_LAND,
    ]),
    landAreaUsePurpose: z.array(z.number()),
    landYousekiRatio: makeRangeSchemaWithRestrictMinMax(
      YOUSEKI_RATIO_MIN,
      YOUSEKI_RATIO_MAX,
      "「以上」は「以下」より小さくしてください"
    ),
    landKenpeiRatio: makeRangeSchemaWithRestrictMinMax(
      KENPEI_RATIO_MIN,
      KENPEI_RATIO_MAX,
      "「以上」は「以下」より小さくしてください"
    ),
    realEstateType: z
      .string()
      .trim()
      .refine((value) => value.length > 0, {
        message: "不動産種別を選択してください",
      }),
    receptionReasons: z.array(z.string()),
    requestDateRange: makeNullableDateRangeSchema(
      requestDateMin,
      maxDate,
      "法務局受付日は2012/01/01以降、かつ当月以前を入力してください",
      "法務局受付日の開始日は終了日以前を入力してください"
    ),
    ownerName: z
      .string()
      .max(
        OWNER_NAME_MAX_LENGTH,
        `${OWNER_NAME_MAX_LENGTH}文字以内で入力してください`
      ),
    ownerAddress: z
      .string()
      .max(
        OWNER_ADDRESS_MAX_LENGTH,
        `${OWNER_ADDRESS_MAX_LENGTH}文字以内で入力してください`
      ),
    landType: z.object({
      farm: z.boolean(),
      solar: z.boolean(),
      parkingLot: z.boolean(),
    }),
    parkingLotName: z
      .string()
      .max(
        PARKING_LOT_NAME_MAX_LENGTH,
        `${PARKING_LOT_NAME_MAX_LENGTH}文字以内で入力してください`
      ),
    totalParkingLotArea: makeAreaRangeSchema(
      "「以上」は「以下」より小さくしてください"
    ),
    estimateAcreage: makeAreaRangeSchema(
      "「以上」は「以下」より小さくしてください"
    ),
    landPrice: makeBigIntRangeSchema(
      "「以上」は「以下」より小さくしてください"
    ),
    landWalk: z.number().nullable(),
    buildingType: z.object({
      mansion: z.boolean(),
      apartment: z.boolean(),
      building: z.boolean(),
      hotel: z.boolean(),
      dormitory: z.boolean(),
      industrialFacility: z.boolean(),
      other: z.boolean(),
    }),
    buildingAreaUsePurpose: z.array(z.number()),
    propertyName: z
      .string()
      .max(
        PROPERTY_NAME_MAX_LENGTH,
        `${PROPERTY_NAME_MAX_LENGTH}文字以内で入力してください`
      ),
    buildingVariant: z.array(z.number()),
    buildDateRange: makeNullableDateRangeSchema(
      BUILD_DATE_MIN,
      maxDate,
      "築年月は1900/01以降、かつ当月以前を入力してください",
      "築年月の開始（以降）は、終了（以前）と同じか前の年月を入力してください"
    ),
    totalFloorArea: makeAreaRangeSchema(
      "「以上」は「以下」より小さくしてください"
    ),
    exclusiveArea: makeAreaRangeSchema(
      "「以上」は「以下」より小さくしてください"
    ),
    groundFloors: makeIntegerRangeSchema(
      GROUND_FLOORS_MIN,
      GROUND_FLOORS_MAX,
      "「以上」は「以下」より小さくしてください"
    ),
    undergroundFloors: makeIntegerRangeSchema(
      UNDERGROUND_FLOORS_MIN,
      UNDERGROUND_FLOORS_MAX,
      "「以上」は「以下」より小さくしてください"
    ),
    parkingSpaces: makeIntegerRangeSchema(
      PARKING_SPACES_MIN,
      PARKING_SPACES_MAX,
      "「以上」は「以下」より小さくしてください"
    ),
    buildingPrice: makeBigIntRangeSchema(
      "「以上」は「以下」より小さくしてください"
    ),
    buildingWalk: z.number().nullable(),
    buildingYousekiRatio: makeRangeSchemaWithRestrictMinMax(
      YOUSEKI_RATIO_MIN,
      YOUSEKI_RATIO_MAX,
      "「以上」は「以下」より小さくしてください"
    ),
    buildingKenpeiRatio: makeRangeSchemaWithRestrictMinMax(
      KENPEI_RATIO_MIN,
      KENPEI_RATIO_MAX,
      "「以上」は「以下」より小さくしてください"
    ),
    buildingAge: makeRangeSchemaWithRestrictMinMax(
      BUILDING_AGE_MIN,
      BUILDING_AGE_MAX,
      "「以上」は「以下」より小さくしてください"
    ),
    kaokuNumber: makeIntegerRangeSchema(
      KAOKU_NUMBER_MIN,
      KAOKU_NUMBER_MAX,
      "「以上」は「以下」より小さくしてください"
    ),
    landArea: makeAreaRangeSchema("「以上」は「以下」より小さくしてください"),
    buildingArea: makeAreaRangeSchema(
      "「以上」は「以下」より小さくしてください"
    ),
    hotelName: z
      .string()
      .max(
        HOTEL_NAME_MAX_LENGTH,
        `${HOTEL_NAME_MAX_LENGTH}文字以内で入力してください`
      ),
    dormitoryName: z
      .string()
      .max(
        DORMITORY_NAME_MAX_LENGTH,
        `${DORMITORY_NAME_MAX_LENGTH}文字以内で入力してください`
      ),
    industrialFacilityName: z
      .string()
      .max(
        INDUSTRIAL_FACILITY_NAME_MAX_LENGTH,
        `${INDUSTRIAL_FACILITY_NAME_MAX_LENGTH}文字以内で入力してください`
      ),
    dormitoryKind: z.array(z.enum(DORMITORY_KIND_VALUES)),
    hotelCategory: z.array(z.number()),
    hotelEstimateAcreage: makeAreaRangeSchema(
      "「以上」は「以下」より小さくしてください"
    ),
    hotelRoomCount: makeIntegerRangeSchema(
      HOTEL_ROOM_COUNT_MIN,
      HOTEL_ROOM_COUNT_MAX,
      "「以上」は「以下」より小さくしてください"
    ),
    ownershipType: z.union([z.literal("bunjou"), z.literal("ittou"), z.null()]),
  });

  // 定義したZodのスキーマをTypescriptの型に変換
  // type FormData = z.infer<typeof FormDataSchema>;

  // 型を用いてReact-Hook-Formのインスタンスを作る
  const rhf = useForm<FormData>({
    mode: "all",
    defaultValues: {
      prefecture: [],
      city: [],
      address: "",
      areaUsePurposeDataSource: defaultAreaUsePurposeDataSource,
      landAreaUsePurpose: [],
      landYousekiRatio: { min: null, max: null },
      landKenpeiRatio: { min: null, max: null },
      realEstateType: "",
      receptionReasons: [],
      requestDateRange: [null, null],
      ownerName: "",
      ownerAddress: "",
      landType: { farm: false, solar: false, parkingLot: false },
      parkingLotName: "",
      totalParkingLotArea: { min: null, max: null },
      estimateAcreage: { min: null, max: null },
      landPrice: { min: null, max: null },
      landWalk: null,
      buildingType: {
        apartment: false,
        mansion: false,
        building: false,
        hotel: false,
        dormitory: false,
        industrialFacility: false,
        other: false,
      },
      buildingAreaUsePurpose: [],
      propertyName: "",
      buildingVariant: [],
      buildDateRange: [null, null],
      totalFloorArea: { min: null, max: null },
      exclusiveArea: { min: null, max: null },
      groundFloors: { min: null, max: null },
      undergroundFloors: { min: null, max: null },
      parkingSpaces: { min: null, max: null },
      buildingPrice: { min: null, max: null },
      buildingWalk: null,
      buildingYousekiRatio: { min: null, max: null },
      buildingKenpeiRatio: { min: null, max: null },
      buildingAge: { min: null, max: null },
      kaokuNumber: { min: null, max: null },
      landArea: { min: null, max: null },
      buildingArea: { min: null, max: null },
      hotelName: "",
      dormitoryName: "",
      industrialFacilityName: "",
      dormitoryKind: [],
      hotelCategory: [],
      hotelEstimateAcreage: { min: null, max: null },
      hotelRoomCount: { min: null, max: null },
      ownershipType: null,
    },
    resolver: zodResolver(FormDataSchema),
  });

  const handleDatePickerErrorChange = (
    fieldName: "requestDateRange" | "buildDateRange",
    label: string,
    index: 0 | 1
  ) => {
    return (error: unknown): void => {
      if (error === null || error === "minDate" || error === "maxDate") {
        void rhf.trigger(fieldName);
        return;
      }
      const errorPathMap = {
        requestDateRange: ["requestDateRange.0", "requestDateRange.1"],
        buildDateRange: ["buildDateRange.0", "buildDateRange.1"],
      } as const;
      const errorPath = errorPathMap[fieldName][index];
      rhf.setError(errorPath, {
        type: "manual",
        message: `${label}の形式が正しくありません`,
      });
    };
  };

  // realEstateType の変更を監視して、アコーディオンとラジオの状態制御に使用
  const watchedRealEstateType = rhf.watch("realEstateType");

  // 駐車場名と駐車場推計地積を監視
  const watchedParkingLotName = rhf.watch("parkingLotName");
  const watchedTotalParkingLotArea = rhf.watch("totalParkingLotArea");

  // 駐車場フィールドに入力があるかを判定
  const hasParkingLotName =
    watchedParkingLotName && watchedParkingLotName.trim() !== "";
  const hasTotalParkingLotArea =
    (watchedTotalParkingLotArea?.min !== null &&
      watchedTotalParkingLotArea?.min !== undefined) ||
    (watchedTotalParkingLotArea?.max !== null &&
      watchedTotalParkingLotArea?.max !== undefined);
  const isParkingLotInputFilled = hasParkingLotName || hasTotalParkingLotArea;

  // 駐車場名または駐車場推計地積に値が入力されたら、自動的に駐車場チェックボックスをON/OFFにする
  useEffect(() => {
    const currentLandType = rhf.getValues("landType");
    if (isParkingLotInputFilled) {
      // 入力がある場合はチェックをONにする
      if (!currentLandType.parkingLot) {
        rhf.setValue("landType", { ...currentLandType, parkingLot: true });
      }
    } else {
      // 入力がない場合はチェックをOFFにする
      if (currentLandType.parkingLot) {
        rhf.setValue("landType", { ...currentLandType, parkingLot: false });
      }
    }
  }, [isParkingLotInputFilled, rhf.setValue]);

  const handleSubmit = rhf.handleSubmit(
    async (data: RealEstateTitleSearchAPIRequest) => {
      resetPage();

      setRealEstateTitles([]);
      setSwitchHasTrade(false);
      setSwitchDisabled(true);
      setLoading(true);

      const result = await feedAPI(apiClient, data);

      if (result?.status === HttpStatusCode.Unauthorized) {
        toast.info("再度検索ボタンを押下してください");
        setLoading(false);
        return;
      } else if (result?.status === HttpStatusCode.Ok && result?.timeout) {
        toast.info(
          "該当するデータが多すぎて処理できませんでした。より詳細な条件で再度検索してください。"
        );
        setLoading(false);
        return;
      } else if (result?.status === HttpStatusCode.BadRequest) {
        toast.error(result?.error);
        return;
      } else if (result?.status === HttpStatusCode.InternalServerError) {
        toast.error("予期せぬエラーが発生しました");
        return;
      }

      const rows: RealEstateTitleSearchDataGridRow[] =
        result?.data?.list.map(
          (item: RealEstateTitleSearchAPIResponseStatus, index) => ({
            id: `${item.realEstateType}${ID_DELIMITER}${item.id}${ID_DELIMITER}${index}`,
            originalId: item.id,
            realEstateType: item.realEstateType,
            landType: item.landType,
            prefecture: item.prefecture,
            prefCode: item.prefCode,
            syozai: item.syozai,
            propertyName: item.propertyName,
            chibanKaokuNo: item.chibanKaokuNo,
            stationAndWalk: item.stationAndWalk,
            areaUsePurposeType: item.areaUsePurposeType,
            latestReceptionDateAndReason: item.latestReceptionDateAndReason,
            estimateAcreage: item.estimateAcreage,
            price: item.price,
            buildingType: item.buildingType,
            kenpeiRatio: item.kenpeiRatio,
            yousekiRatio: item.yousekiRatio,
            parkingLotName: item.parkingLotName,
            parkingLotAccommodation: item.parkingLotAccommodation,
            totalParkingLotArea: item.totalParkingLotArea,
            buildingVariant: item.buildingVariant,
            buildingAge: item.buildingAge,
            groundFloors: item.groundFloors,
            undergroundFloors: item.undergroundFloors,
            totalFloorArea: item.totalFloorArea,
            kaokuNumber: item.kaokuNumber,
            landArea: item.landArea,
            buildingArea: item.buildingArea,
            parkingSpaces: item.parkingSpaces,
            buildingExclusiveAreaMin: item.buildingExclusiveAreaMin,
            buildingExclusiveAreaMax: item.buildingExclusiveAreaMax,
            buildingDetailAddress: item.buildingDetailAddress,
            buildingBuildDate: item.buildingBuildDate,
            buildingTotalUnits: item.buildingTotalUnits,
            buildingMinLayout: item.buildingMinLayout,
            buildingMaxLayout: item.buildingMaxLayout,
            buildingMainDirection: item.buildingMainDirection,
            buildingNeighborhoodOtherInformation:
              item.buildingNeighborhoodOtherInformation,
            hotelName: item.hotelName,
            dormitoryName: item.dormitoryName,
            industrialFacilityName: item.industrialFacilityName,
            hotelCategory: item.hotelCategory,
            hotelEstimateAcreage: item.hotelEstimateAcreage,
            hotelRoomCount: item.hotelRoomCount,
          })
        ) ?? [];
      setRealEstateTitles(rows);
      setSwitchDisabled(rows.length > MAX_HAS_TRADE_COUNT);
      setLoading(false);
    }
  );

  // 都道府県の変更を検知する
  const prefWatch = rhf.watch("prefecture");

  // 選択している都道府県に応じた市区町村リストを取得する
  const { cityList } = useCityWithExternalState(
    prefWatch.map((value) => value.toString()).filter((value) => value !== "")
  );

  const handleClickAcquireButton = (): void => {
    const selected = realEstateTitles.filter((row) => row.selected);
    if (selected.length === 0) {
      toast.info("取得対象が選択されていません");
      return;
    }

    setAcquireMultipleBooksRows(
      selected.map((row) => {
        return {
          id: row.originalId,
          bookType: getBookTypeFromRealEstateTypeLabel(row.realEstateType),
          prefCode: row.prefCode,
          prefName: row.prefecture,
          locationFull: row.syozai,
          chibanKaokuNumber: row.chibanKaokuNo,
          kyoutan: false,
          sintaku: false,
          genzai: false,
          ownerInfo: false,
          electricDrawings: false,
          tisekiDrawings: false,
          tiekiDrawings: false,
          buildingDrawings: false,
        };
      })
    );

    setOpenAcquireMultipleBooksModal(true);
  };

  const handleSelectHistory = async (
    history: UserSearchHistoryTitleResponseType
  ): Promise<void> => {
    // 日付を時刻情報なしの純粋な日付に変換する
    const toDateWithoutTime = (dateStr: string | null): Date | null => {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      date.setHours(0, 0, 0, 0);
      return date;
    };

    // ホテル関連の履歴値（フラグOFF時はデフォルト値を使用）
    const historyHotel = history.buildingTypes?.hotel ?? false;
    const historyDormitory = history.buildingTypes?.dormitory ?? false;
    const historyIndustrialFacility =
      history.buildingTypes?.industrialFacility ?? false;
    const historyHotelName = history.hotelName ?? "";
    const historyDormitoryName = history.dormitoryName ?? "";
    const historyIndustrialFacilityName = history.industrialFacilityName ?? "";
    const historyDormitoryKind = history.dormitoryKind ?? [];
    const historyHotelCategory = history.hotelCategory ?? [];
    const historyHotelEstimateAcreageMin =
      history.hotelEstimateAcreageMin ?? null;
    const historyHotelEstimateAcreageMax =
      history.hotelEstimateAcreageMax ?? null;
    const historyHotelRoomCountMin = history.hotelRoomCountMin ?? null;
    const historyHotelRoomCountMax = history.hotelRoomCountMax ?? null;

    const formValues = {
      // 都道府県
      prefecture: history.prefectures ?? [],
      // 市区町村
      city: history.cities ?? [],
      // 大字・丁目
      address: history.ooaza ?? "",
      // 用途地域データソース
      areaUsePurposeDataSource:
        history.areaUsePurposeDataSource ?? defaultAreaUsePurposeDataSource,
      // 不動産種別
      realEstateType: history.realEstateType ?? "",
      // 登記原因
      receptionReasons: history.receptionReasons ?? [],
      // 法務局受付日
      requestDateRange: [
        history.legalAffairsBureauRequestDateStart
          ? toDateWithoutTime(history.legalAffairsBureauRequestDateStart)
          : null,
        history.legalAffairsBureauRequestDateEnd
          ? toDateWithoutTime(history.legalAffairsBureauRequestDateEnd)
          : null,
      ] as [Date | null, Date | null],
      // 所有者名
      ownerName: history.ownerName ?? "",
      // 所有者住所
      ownerAddress: history.ownerAddress ?? "",

      // 土地種別
      landType: {
        farm: history.landTypes?.farm ?? false,
        solar: history.landTypes?.solar ?? false,
        parkingLot: history.landTypes?.parkingLot ?? false,
      },
      // 駐車場名
      parkingLotName: history.parkingLotName ?? "",
      // 駐車場推計地積
      totalParkingLotArea: {
        min: history.totalParkingLotAreaMin ?? null,
        max: history.totalParkingLotAreaMax ?? null,
      },
      // 土地の用途地域
      landAreaUsePurpose: history.landAreaUsePurposeTypes ?? [],
      // 推計面積
      estimateAcreage: {
        min: history.landEstimateAcreageMin ?? null,
        max: history.landEstimateAcreageMax ?? null,
      },
      // 公示価格（土地）
      landPrice: {
        min: history.landPublicLandPriceMin ?? null,
        max: history.landPublicLandPriceMax ?? null,
      },
      // 容積率（土地）
      landYousekiRatio: {
        min: history.landYosekiRatioMin ?? null,
        max: history.landYosekiRatioMax ?? null,
      },
      // 建ぺい率（土地）
      landKenpeiRatio: {
        min: history.landBuildingRatioMin ?? null,
        max: history.landBuildingRatioMax ?? null,
      },
      // 最寄駅からの徒歩時間（土地）
      landWalk: history.landWalk ? Number(history.landWalk) : null,

      // 建物種別
      buildingType: {
        mansion: history.buildingTypes?.mansion ?? false,
        apartment: history.buildingTypes?.apartment ?? false,
        building: history.buildingTypes?.building ?? false,
        hotel: hotelSearchEnabled ? historyHotel : false,
        dormitory: dormitorySearchEnabled ? historyDormitory : false,
        industrialFacility: industrialFacilitySearchEnabled
          ? historyIndustrialFacility
          : false,
        other: history.buildingTypes?.other ?? false,
      },
      // 構造
      buildingVariant: history.buildingStructure ?? [],
      // 建物の用途地域
      buildingAreaUsePurpose: history.buildingAreaUsePurposeTypes ?? [],
      // 延べ床面積
      totalFloorArea: {
        min: history.buildingTotalFloorAreaMin ?? null,
        max: history.buildingTotalFloorAreaMax ?? null,
      },
      // 築年月
      buildDateRange: [
        history.buildingBuildDateAfter
          ? toDateWithoutTime(history.buildingBuildDateAfter)
          : null,
        history.buildingBuildDateBefore
          ? toDateWithoutTime(history.buildingBuildDateBefore)
          : null,
      ] as [Date | null, Date | null],
      // 築年数
      buildingAge: {
        min: history.buildingBuildAgeMin ?? null,
        max: history.buildingBuildAgeMax ?? null,
      },
      // 建物名
      propertyName: history.buildingPropertyName ?? "",
      // 専有面積
      exclusiveArea: {
        min: history.buildingExclusiveAreaMin ?? null,
        max: history.buildingExclusiveAreaMax ?? null,
      },
      // 地上階数
      groundFloors: {
        min: history.buildingGroundFloorsMin ?? null,
        max: history.buildingGroundFloorsMax ?? null,
      },
      // 地下階数
      undergroundFloors: {
        min: history.buildingUndergroundFloorsMin ?? null,
        max: history.buildingUndergroundFloorsMax ?? null,
      },
      // 駐車場数
      parkingSpaces: {
        min: history.buildingParkingSpacesMin ?? null,
        max: history.buildingParkingSpacesMax ?? null,
      },
      // 土地面積
      landArea: {
        min: history.buildingLandAreaMin ?? null,
        max: history.buildingLandAreaMax ?? null,
      },
      // 建物面積
      buildingArea: {
        min: history.buildingAreaMin ?? null,
        max: history.buildingAreaMax ?? null,
      },
      // 公示価格（建物）
      buildingPrice: {
        min: history.buildingPublicLandPriceMin ?? null,
        max: history.buildingPublicLandPriceMax ?? null,
      },
      // 最寄駅からの徒歩時間（建物）
      buildingWalk: history.buildingWalk ? Number(history.buildingWalk) : null,
      // 家屋番号数
      kaokuNumber: {
        min: history.buildingKaokuNumberMin ?? null,
        max: history.buildingKaokuNumberMax ?? null,
      },
      // 容積率（建物）
      buildingYousekiRatio: {
        min: history.buildingYosekiRatioMin ?? null,
        max: history.buildingYosekiRatioMax ?? null,
      },
      // 建ぺい率（建物）
      buildingKenpeiRatio: {
        min: history.buildingBuildingRatioMin ?? null,
        max: history.buildingBuildingRatioMax ?? null,
      },
      // ホテル名
      hotelName: hotelSearchEnabled ? historyHotelName : "",
      // 寮・社宅名
      dormitoryName: dormitorySearchEnabled ? historyDormitoryName : "",
      // 工場/倉庫名
      industrialFacilityName: industrialFacilitySearchEnabled
        ? historyIndustrialFacilityName
        : "",
      // 寮・社宅 種別
      dormitoryKind: dormitorySearchEnabled ? historyDormitoryKind : [],
      // ホテル形態
      hotelCategory: hotelSearchEnabled ? historyHotelCategory : [],
      // ホテル 推計地積
      hotelEstimateAcreage: {
        min: hotelSearchEnabled ? historyHotelEstimateAcreageMin : null,
        max: hotelSearchEnabled ? historyHotelEstimateAcreageMax : null,
      },
      // ホテル 部屋数
      hotelRoomCount: {
        min: hotelSearchEnabled ? historyHotelRoomCountMin : null,
        max: hotelSearchEnabled ? historyHotelRoomCountMax : null,
      },
      // 分譲・一棟
      ownershipType: ((): "bunjou" | "ittou" | null => {
        const value = history.ownershipType;
        if (value === "bunjou" || value === "ittou") {
          return value;
        }
        return null;
      })(),
    };

    // フォームの状態を完全にリセットして新しい値を設定
    rhf.reset(formValues, { keepValues: false });
    await rhf.trigger();
    setOpenSearchHistoryModal(false);
  };

  if (!realEstateTitle) return null;

  return (
    <PagePaper>
      {/* ページタイトル */}
      <Box sx={{ display: "flex", mb: 3 }}>
        <LocationCityIcon sx={{ mr: 1 }} fontSize="large" />
        <PageTitle>物件種別から検索する</PageTitle>
      </Box>
      <SectionPaper>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <SectionTitle>検索</SectionTitle>
        </Box>
        <Box sx={{ mx: 2 }}>
          <Divider />
        </Box>
        <Box sx={{ mx: 2, my: 2 }}>
          <AlertTitle
            sx={{
              fontWeight: "normal",
              color: "text.primary",
              fontSize: "0.85rem",
            }}
          >
            ・検索に時間が60秒程度かかる場合があります
          </AlertTitle>
          <AlertTitle
            sx={{
              fontWeight: "normal",
              color: "text.primary",
              fontSize: "0.85rem",
            }}
          >
            ・検索にタイムアウトした場合は、さらに条件を絞り込んで再度検索してください
          </AlertTitle>
          <Box sx={{ my: 2 }}>
            <Button
              label="検索履歴"
              onClick={() => {
                setOpenSearchHistoryModal(true);
              }}
              variant={ButtonVariantOption.Outlined}
              size={SizingWrapperStyle.SMALL}
            />
          </Box>
          <Grid container direction="column" rowSpacing={2}>
            <Grid item xs={12}>
              <Label required={true}>都道府県</Label>
              <Controller
                control={rhf.control}
                name="prefecture"
                render={({ field: { value, onChange }, fieldState }) => {
                  return (
                    <MultiSelect
                      error={Boolean(fieldState.error)}
                      formHelperText={fieldState.error?.message}
                      label="都道府県"
                      value={value.map((v) => v.toString())}
                      onChange={(vals) => {
                        onChange(vals.map((v) => Number(v)));
                        /* eslint-disable */
                        // @ts-ignore
                        // 市区町村の選択をリセットする
                        rhf.setValue("city", []);
                      }}
                      options={prefectures.map((pref) => ({
                        label: pref.name,
                        value: pref.prefCode,
                      }))}
                      maxSelect={2}
                    />
                  );
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Label required={true}>市区町村</Label>
              <Controller
                control={rhf.control}
                name="city"
                render={({ field: { value, onChange }, fieldState }) => {
                  return (
                    <MultiSelect
                      error={Boolean(fieldState.error)}
                      formHelperText={fieldState.error?.message}
                      label="市区町村"
                      value={value.map((v) => v.toString())}
                      onChange={(vals) => {
                        onChange(vals.map((v) => Number(v)));
                      }}
                      options={(cityList?.list ?? []).map((city) => ({
                        label: city.name,
                        value: city.id.toString(),
                      }))}
                      maxSelect={2}
                    />
                  );
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Label>大字・町丁目</Label>
              <Controller
                control={rhf.control}
                name="address"
                render={({ field: { value, onChange }, fieldState }) => {
                  return (
                    <TextField
                      label="大字・町丁目"
                      value={value}
                      onChange={onChange}
                      placeholder="大字・町丁目"
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      sx={{ width: 250 }}
                    />
                  );
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Label>登記原因</Label>
              <CMultiSelect<FormData>
                name="receptionReasons"
                control={rhf.control}
                label="登記原因"
                options={receptionReasonOptions}
                maxSelect={3}
              />
            </Grid>
            <Grid item xs={12}>
              <Label>法務局受付日</Label>
              <Controller
                control={rhf.control}
                name="requestDateRange"
                render={({ field: { value, onChange }, fieldState }) => {
                  const rangeErrors = fieldState.error as
                    | Array<{ message?: string } | undefined>
                    | undefined;
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <DateRangePicker
                        startDateLabel="法務局受付日 開始"
                        startDate={value[0]}
                        onChangeStartDate={(date) => {
                          onChange([date, value[1]]);
                        }}
                        endDateLabel="法務局受付日 終了"
                        endDate={value[1]}
                        onChangeEndDate={(date) => {
                          onChange([value[0], date]);
                        }}
                        onStartDateErrorChange={handleDatePickerErrorChange(
                          "requestDateRange",
                          "法務局受付日",
                          0
                        )}
                        onEndDateErrorChange={handleDatePickerErrorChange(
                          "requestDateRange",
                          "法務局受付日",
                          1
                        )}
                        minDate={requestDateMin}
                        maxDate={maxDate}
                        views={["year", "month", "day"]}
                        errorStart={rangeErrors?.[0]?.message}
                        errorEnd={rangeErrors?.[1]?.message}
                      />
                    </Box>
                  );
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Label>所有者情報</Label>
              <Box sx={{ display: "flex", gap: 3 }}>
                <Box>
                  <Label
                    sx={{ fontWeight: "normal", fontSize: "0.875rem", mb: 0.5 }}
                  >
                    所有者名 (部分一致)
                  </Label>
                  <TextField
                    {...rhf.register("ownerName")}
                    placeholder="所有者名"
                    error={Boolean(rhf.formState.errors.ownerName)}
                    helperText={rhf.formState.errors.ownerName?.message}
                    sx={{ width: 250 }}
                  />
                </Box>
                <Box>
                  <Label
                    sx={{ fontWeight: "normal", fontSize: "0.875rem", mb: 0.5 }}
                  >
                    所有者住所 (部分一致)
                  </Label>
                  <TextField
                    {...rhf.register("ownerAddress")}
                    placeholder="所有者住所"
                    error={Boolean(rhf.formState.errors.ownerAddress)}
                    helperText={rhf.formState.errors.ownerAddress?.message}
                    sx={{ width: 250 }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mx: 2 }}>
          <Label required={true}>不動産種別ごとの検索条件</Label>
        </Box>

        <Box sx={{ mx: 2, mb: 2 }}>
          <LandSearchAccordion
            control={rhf.control}
            watchedRealEstateType={watchedRealEstateType}
            setValue={rhf.setValue}
            formState={rhf.formState}
            areaUsePurposeOptions={areaUsePurposeOptions}
            isParkingLotInputFilled={isParkingLotInputFilled}
            cityPlanningAreaUsePurposeSearch={cityPlanningAreaUsePurposeSearch}
          />
        </Box>

        <Box sx={{ mx: 2, mb: 2 }}>
          <BuildingSearchAccordion
            control={rhf.control}
            watchedRealEstateType={watchedRealEstateType}
            setValue={rhf.setValue}
            onBuildDateStartErrorChange={handleDatePickerErrorChange(
              "buildDateRange",
              "築年月",
              0
            )}
            onBuildDateEndErrorChange={handleDatePickerErrorChange(
              "buildDateRange",
              "築年月",
              1
            )}
            formState={rhf.formState}
            minDate={BUILD_DATE_MIN}
            maxDate={maxDate}
            areaUsePurposeOptions={areaUsePurposeOptions}
            hotelCategoryOptions={hotelCategoryOptions}
            dormitoryKindOptions={DORMITORY_KIND_OPTIONS}
            hotelSearch={hotelSearchEnabled}
            dormitorySearch={dormitorySearchEnabled}
            industrialFacilitySearch={industrialFacilitySearchEnabled}
            watch={rhf.watch}
            cityPlanningAreaUsePurposeSearch={cityPlanningAreaUsePurposeSearch}
          />
        </Box>
        <Divider />
        <Box sx={{ my: 3, mr: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            label="この条件で検索"
            onClick={() => {
              (async () => {
                await handleSubmit();
              })();
            }}
            variant={ButtonVariantOption.Contained}
            size={SizingWrapperStyle.SMALL}
            disabled={rhf.formState.isSubmitting || !rhf.formState.isValid}
          />
        </Box>
      </SectionPaper>

      <Box sx={{ mt: 3 }} />

      {/* 不動産情報一覧テーブル */}
      <SectionPaper>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems="center"
        >
          <Box display={"flex"} justifyContent={"center"}>
            <SectionTitle>不動産情報一覧</SectionTitle>
          </Box>
          <Tooltip
            title={`1度で登記/図面取得できる不動産は${formatNumberWithComma(
              MAX_HAS_TRADE_COUNT
            )}件までです`}
            placement="top"
          >
            <Stack mx={2} my={2} direction="row" spacing={1}>
              <span style={{ display: "inline-flex" }}>
                <MuiButton
                  disabled={
                    realEstateTitles.filter((row) => row.selected).length ===
                      0 ||
                    realEstateTitles.filter((row) => row.selected).length >
                      MAX_HAS_TRADE_COUNT
                  }
                  onClick={() => {
                    handleClickAcquireButton();
                  }}
                  variant="outlined"
                  sx={{ fontWeight: "bold" }}
                >
                  不動産登記/図面
                  <br />
                  取得（一括）
                </MuiButton>
              </span>
            </Stack>
          </Tooltip>
        </Stack>

        <FeedGrid
          loading={loading}
          rows={realEstateTitles}
          setRows={setRealEstateTitles}
          setOpen={setOpenRealEstateDetailModal}
          setDetailData={setDetailData}
          displayHasTrade={switchHasTrade}
          page={page}
          pageSize={pageSize}
          changePage={changePage}
          changePageSize={changePageSize}
          rowsPerPageOptions={rowsPerPageOptions}
        />

        <Box sx={{ mb: 6 }} />
      </SectionPaper>

      {/* 戻る */}
      <Box sx={{ mt: 2 }}>
        <Link
          component={RouterLink}
          to={mypageTop ? "/mypage" : "/feed"}
          sx={{ display: "inline-block" }}
        >
          <Box sx={{ display: "flex" }}>
            <KeyboardArrowLeft />
            <Typography>
              {mypageTop ? "マイページに戻る" : "不動産の権利異動検索に戻る"}
            </Typography>
          </Box>
        </Link>
      </Box>
      {/* モーダルを表示する際に使用するコンポーネント */}
      <RealEstateDetailModal
        open={openRealEstateDetailModal}
        setOpen={setOpenRealEstateDetailModal}
        fullWidth={true}
        maxWidth={"md"}
        title={"不動産情報詳細"}
        contents={detailData}
      />
      {/* モーダルを表示する際に使用するコンポーネント */}
      <Modal
        open={openAcquireMultipleBooksModal}
        aria-labelledby={"acquire-multiple-books-modal"}
        aria-describedby={"acquire-multiple-books-modal"}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            outline: "none",
          }}
        >
          <Box
            sx={{
              width: 1600,
              maxWidth: "90%",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 2,
              overflow: "auto",
              maxHeight: "calc(100vh - 64px)",
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <DialogActions disableSpacing={true}>
              <Box mx={2}>
                <Button
                  label="閉じる"
                  variant={ButtonVariantOption.Outlined}
                  onClick={() => {
                    setOpenAcquireMultipleBooksModal(false);
                  }}
                  size={SizingWrapperStyle.SMALL}
                  startIcon={<CloseIcon />}
                />
              </Box>
            </DialogActions>
            <AcquireMultipleBooksModal
              handleSubmit={acquireBookFormHandleSubmit}
              rows={acquireMultipleBooksRows}
              isOpenedTitleSearch={true}
            />
          </Box>
        </Box>
      </Modal>
      <SearchHistoryModal
        open={openSearchHistoryModal}
        onClose={() => {
          setOpenSearchHistoryModal(false);
        }}
        onSelect={handleSelectHistory}
      />
    </PagePaper>
  );
};
